// Where a composer popover opens on desktop, and how tall (STACK-565).
//
// Round 7 (Aravind, 2026-10-09; supersedes round 5's scroll-first rule, which
// felt janky — a menu should not move the page):
//
//   - `planMenu` (the Medications panel), like Perplexity's model menu: never
//     scrolls. It opens below when its full height (near enough: 20rem of
//     the 23rem cap) fits there, and otherwise
//     on the side with more room — upward, it overlays the hero and the goal
//     box, which is fine because focus is in the panel and Done, Escape and a
//     click outside close it. Its height is the smaller of the cap and that
//     side's room, never below the few-results minimum.
//   - `planBelow` (the goal list): always below the composer card, because it
//     suggests for the text being typed and must not cover the input. It uses
//     the room below whenever a few goals fit, and scrolls the page only when
//     fewer would.
//
// Pure arithmetic over the anchor's rect and the page's scroll metrics, so a
// test can drive it; the caller applies any scroll. Phones never use this:
// they get a bottom sheet.

export const POPOVER_CAP_PX = 368; // 23rem
// Room below that counts as "the full height fits": 20rem. Below that the
// menu would open noticeably short, so it takes the roomier side instead.
export const MENU_FITS_BELOW_PX = 320;
export const POPOVER_GAP_PX = 8;
const EDGE_MARGIN_PX = 16;
// Keeps a popover (or a scrolled-up card) clear of the app bar (4rem).
const TOP_CLEARANCE_PX = 80;

const viewportBounds = () => {
  const vv = window.visualViewport;
  const top = vv ? vv.offsetTop : 0;
  return { top, bottom: vv ? vv.offsetTop + vv.height : window.innerHeight };
};

const roomAround = (rect, view) => ({
  below: view.bottom - rect.bottom - POPOVER_GAP_PX - EDGE_MARGIN_PX,
  above: rect.top - view.top - TOP_CLEARANCE_PX - POPOVER_GAP_PX,
});

/**
 * A menu that never moves the page.
 * @returns {{ scrollBy: 0, placement: 'bottom-start'|'top-start', maxHeightPx: number }}
 */
export function planMenu(anchorEl, { minPx, capPx = POPOVER_CAP_PX } = {}) {
  const { below, above } = roomAround(anchorEl.getBoundingClientRect(), viewportBounds());
  if (below >= Math.min(capPx, MENU_FITS_BELOW_PX)) {
    return { scrollBy: 0, placement: 'bottom-start', maxHeightPx: Math.min(capPx, below) };
  }
  const up = above > below;
  const room = up ? above : below;
  return {
    scrollBy: 0,
    placement: up ? 'top-start' : 'bottom-start',
    maxHeightPx: Math.min(capPx, Math.max(minPx, room)),
  };
}

/**
 * A list that always opens below its anchor, scrolling only when fewer than
 * `minPx` would fit — and then only as far as the full height needs, without
 * pushing the anchor's top under the app bar.
 * @returns {{ scrollBy: number, placement: 'bottom-start', maxHeightPx: number }}
 */
export function planBelow(anchorEl, { minPx, capPx = POPOVER_CAP_PX } = {}) {
  const rect = anchorEl.getBoundingClientRect();
  const view = viewportBounds();
  const { below } = roomAround(rect, view);
  let scrollBy = 0;
  if (below < minPx) {
    const page = document.scrollingElement || document.documentElement;
    const scrollable = Math.max(0, page.scrollHeight - window.innerHeight - window.scrollY);
    const anchorLimit = Math.max(0, rect.top - view.top - TOP_CLEARANCE_PX);
    scrollBy = Math.max(0, Math.min(capPx - below, anchorLimit, scrollable));
  }
  return {
    scrollBy,
    placement: 'bottom-start',
    maxHeightPx: Math.min(capPx, Math.max(minPx, below + scrollBy)),
  };
}

/** Smooth-scrolls by the plan's amount; a pixel or two is not worth a jump. */
export function applyPopoverScroll(plan) {
  if (plan.scrollBy > 4) window.scrollBy({ top: plan.scrollBy, behavior: 'smooth' });
}
